import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ApiError } from '../../api/client';
import { applyTheme, logoUrl } from '../../theme/applyTheme';
import { AA_CONTRAST, MIN_CONTRAST, contrastRatio } from '../../theme/contrast';
import { PRESETS } from '../../theme/presets';
import type { Theme, ThemeInput, ThemeScope } from '../../theme/presets';
import { SAVE_DELAY_MS, saveTheme, themeKey, useResetPlannerTheme, useTheme, useThemeFiles } from '../../theme/api';
import { SheetPreview } from './SheetPreview';
import { ColorField, FontSelect } from './ThemeFields';

type ThemeEditorProps = {
  scope: ThemeScope;
};

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function toInput(theme: Theme): ThemeInput {
  const { presetKey, background, foregroundColor, foregroundOpacity, textColor, accentColor, fontHeading, fontBody, titleOutline } = theme;
  return {
    presetKey,
    background,
    foregroundColor,
    foregroundOpacity,
    textColor,
    accentColor,
    fontHeading,
    fontBody,
    titleOutline,
  };
}

function uploadError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 413) return 'Dit bestand is groter dan 6 MB. Sla de rand op als JPG of verklein hem.';
    if (error.status === 415) return 'Alleen PNG-, JPG- of WebP-afbeeldingen kunnen worden gebruikt.';
  }
  return 'Uploaden is niet gelukt. Probeer het opnieuw.';
}

/** One theme (the website or the timetable), edited live: the page changes at once, the save follows. */
export function ThemeEditor({ scope }: ThemeEditorProps) {
  const theme = useTheme(scope);
  const queryClient = useQueryClient();
  const files = useThemeFiles(scope);
  const reset = useResetPlannerTheme();
  const site = useTheme('site').data;
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const timer = useRef<number | undefined>(undefined);
  // A change that is waiting for its save. Leaving the panel or closing the page sends it instead of dropping it.
  const pending = useRef<ThemeInput | null>(null);

  useEffect(() => {
    const flush = (keepalive: boolean) => {
      window.clearTimeout(timer.current);
      if (pending.current) {
        void saveTheme(pending.current, scope, keepalive ? { keepalive: true } : {}).catch(() => undefined);
        pending.current = null;
      }
    };
    const onPageHide = () => flush(true);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      flush(false);
    };
  }, [scope]);

  if (theme.isPending) return <p>Laden…</p>;
  if (theme.isError) return <p role="alert">Het thema kon niet worden geladen. Vernieuw de pagina.</p>;
  const current = theme.data;

  if (scope === 'planner' && current.inherited) {
    return (
      <div className="flex flex-col gap-3">
        <p>
          Het weekrooster volgt nu het thema van de website. Geef het een eigen uiterlijk, bijvoorbeeld een lichter vel op een donkere
          website.
        </p>
        <Button
          className="min-h-11 self-start"
          onClick={async () => {
            if (!site) return;
            queryClient.setQueryData(themeKey('planner'), await saveTheme(toInput(site), 'planner'));
          }}
        >
          Eigen thema voor het weekrooster
        </Button>
      </div>
    );
  }

  const ratio = contrastRatio(current.textColor, current.foregroundColor);

  function commit(next: Theme) {
    window.clearTimeout(timer.current);
    pending.current = null;
    setSaveState('saving');
    saveTheme(toInput(next), scope)
      .then((saved) => {
        queryClient.setQueryData(themeKey(scope), saved);
        setSaveState('saved');
      })
      .catch(() => setSaveState('error'));
  }

  /**
   * Live: the page changes at once. A deliberate choice (preset, background, font) is saved straight away;
   * a continuous one (colour picker, slider) waits a moment so it is not saved on every step.
   */
  function change(patch: Partial<ThemeInput>, options: { keepPreset?: boolean; immediate?: boolean } = {}) {
    const next: Theme = {
      ...current,
      ...patch,
      presetKey: patch.presetKey ?? (options.keepPreset ? current.presetKey : 'eigen'),
    };
    queryClient.setQueryData(themeKey(scope), next);
    if (scope === 'site') applyTheme(next);
    window.clearTimeout(timer.current);
    if (contrastRatio(next.textColor, next.foregroundColor) < MIN_CONTRAST) {
      pending.current = null;
      setSaveState('error');
      return;
    }
    if (options.immediate) {
      commit(next);
      return;
    }
    pending.current = toInput(next);
    setSaveState('saving');
    timer.current = window.setTimeout(() => commit(next), SAVE_DELAY_MS);
  }

  const pickFile = (kind: 'background' | 'logo') => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) files.upload.mutate({ kind, file });
  };

  const statusText: Record<SaveState, string> = {
    idle: '',
    saving: 'Opslaan…',
    saved: 'Opgeslagen',
    error:
      ratio < MIN_CONTRAST ? 'Niet opgeslagen: tekst en voorgrond hebben te weinig contrast.' : 'Opslaan mislukt. Probeer het opnieuw.',
  };
  const logo = logoUrl(current);

  return (
    <div className="flex flex-col gap-6">
      {scope === 'planner' && (
        <>
          <SheetPreview theme={current} />
          <Button variant="link" className="min-h-11 self-start px-0" onClick={() => reset.mutate()}>
            Terug naar het thema van de website
          </Button>
        </>
      )}
      <p role="status" className="min-h-6 text-sm opacity-80">
        {statusText[saveState]}
      </p>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-lg font-bold">Voorinstelling</legend>
        <div className="grid grid-cols-2 gap-2">
          {PRESETS.map((preset) => {
            const chosen = current.presetKey === preset.key;
            return (
              <Button
                key={preset.key}
                variant="outline"
                aria-pressed={chosen}
                onClick={() => change({ ...preset.values, titleOutline: current.titleOutline }, { keepPreset: true, immediate: true })}
                className="min-h-11 justify-start gap-2 aria-pressed:border-[var(--accent)] aria-pressed:ring-2 aria-pressed:ring-[var(--accent)]"
              >
                <span
                  aria-hidden="true"
                  className="size-5 shrink-0 rounded-full border"
                  style={{ background: `linear-gradient(135deg, ${preset.values.foregroundColor} 50%, ${preset.values.accentColor} 50%)` }}
                />
                {preset.label}
                {chosen && <Check aria-hidden="true" className="ml-auto" />}
              </Button>
            );
          })}
        </div>
        <p className="text-sm opacity-70">Een voorinstelling zet alles in één keer; daarna kun je alles nog bijstellen.</p>

        <p className="mt-2 text-sm font-semibold">Achtergrond</p>
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              aria-label={`Achtergrond ${key}`}
              aria-pressed={current.background === `preset:${key}`}
              onClick={() => change({ background: `preset:${key}` }, { immediate: true })}
              className="flex h-14 w-24 items-end justify-center rounded border border-white/40 pb-1"
              style={{
                backgroundImage: `url(/backgrounds/${key}.svg)`,
                backgroundSize: 'cover',
                outline: current.background === `preset:${key}` ? '2px solid var(--accent)' : undefined,
                outlineOffset: 2,
              }}
            >
              <span className="rounded bg-black/55 px-1 text-xs text-white">{label}</span>
            </button>
          ))}
          <Button variant="outline" className="min-h-11" aria-pressed={current.background === null} onClick={() => change({ background: null }, { immediate: true })}>
            Geen
          </Button>
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-lg font-bold">Eigen thema</legend>
        <p className="text-sm opacity-70">Bijvoorbeeld een rand uit Canva: upload je eigen afbeelding, je logo en kies een lettertype.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="min-h-11 cursor-pointer">
            <label>
              Rand of achtergrond kiezen
              <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={pickFile('background')} />
            </label>
          </Button>
          {current.background === 'upload' && (
            <Button variant="link" className="min-h-11 px-0" onClick={() => files.remove.mutate('background')}>
              Eigen achtergrond verwijderen
            </Button>
          )}
        </div>
        <p className="text-sm opacity-70">PNG, JPG of WebP, maximaal 6 MB; voor een rand op bordformaat werkt JPG het best.</p>

        {scope === 'site' && (
          <div className="flex flex-col gap-2">
            {logo && <img src={logo} alt="Klaslogo" className="max-h-24 w-auto self-start rounded" />}
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="outline" className="min-h-11 cursor-pointer">
                <label>
                  {logo ? 'Ander logo kiezen' : 'Logo kiezen'}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={pickFile('logo')} />
                </label>
              </Button>
              {logo && (
                <Button variant="link" className="min-h-11 px-0" onClick={() => files.remove.mutate('logo')}>
                  Logo verwijderen
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          <FontSelect label="Koppen" value={current.fontHeading} onChange={(value) => change({ fontHeading: value }, { immediate: true })} />
          <FontSelect label="Tekst" value={current.fontBody} onChange={(value) => change({ fontBody: value }, { immediate: true })} />
        </div>

        <label className="flex min-h-11 items-center justify-between gap-3">
          <span>
            Omlijnde titel <span className="block text-xs opacity-70">Leesbaar over een drukke rand</span>
          </span>
          <Switch
            aria-label="Omlijnde titel"
            checked={current.titleOutline}
            onCheckedChange={(checked) => change({ titleOutline: checked }, { immediate: true, keepPreset: true })}
          />
        </label>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-lg font-bold">Kleuren</legend>
        <div className="flex flex-wrap items-center gap-4">
          <ColorField label="Voorgrondkleur" value={current.foregroundColor} onChange={(value) => change({ foregroundColor: value })} />
          <label className="flex items-center gap-2">
            Doorzichtigheid
            <input
              type="range"
              min={0}
              max={100}
              value={current.foregroundOpacity}
              aria-label="Doorzichtigheid voorgrond"
              onChange={(event) => change({ foregroundOpacity: Number(event.target.value) })}
            />
            <span className="w-10 text-sm">{current.foregroundOpacity}%</span>
          </label>
          <ColorField label="Tekstkleur" value={current.textColor} onChange={(value) => change({ textColor: value })} />
          <ColorField label="Accentkleur" value={current.accentColor} onChange={(value) => change({ accentColor: value })} />
        </div>
        <p role={ratio < AA_CONTRAST ? 'alert' : undefined} className="text-sm">
          Contrast tekst/voorgrond: {ratio.toFixed(1).replace('.', ',')}:1.{' '}
          {ratio < MIN_CONTRAST
            ? 'Veel te laag: dit wordt niet opgeslagen.'
            : ratio < AA_CONTRAST
              ? 'Let op: onder 4,5:1 is de tekst lastig te lezen.'
              : 'Goed leesbaar.'}
        </p>
      </fieldset>

      {(files.upload.isError || files.remove.isError) && (
        <p role="alert" className="text-sm">
          {uploadError(files.upload.error ?? files.remove.error)}
        </p>
      )}
    </div>
  );
}
